/* ==========================================================================
   Compression Ratio Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-crc]');
  if (!root) return;

  var CM_PER_INCH = 2.54;
  var MM_PER_INCH = 25.4;
  var CC_PER_CI   = 16.387064;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function cylinderCc(diaCm, heightCm) {
    return Math.PI * Math.pow(diaCm / 2, 2) * heightCm;
  }

  function compute(input) {
    var boreIn   = input.boreUnit   === 'mm' ? input.bore   / MM_PER_INCH : input.bore;
    var strokeIn = input.strokeUnit === 'mm' ? input.stroke / MM_PER_INCH : input.stroke;

    if (!isFinite(boreIn) || boreIn <= 0)     return { ok: false, errors: ['Enter the bore.'] };
    if (!isFinite(strokeIn) || strokeIn <= 0) return { ok: false, errors: ['Enter the stroke.'] };
    if (!isFinite(input.chamber) || input.chamber <= 0) {
      return { ok: false, errors: ['Enter the combustion chamber volume in cc.'] };
    }
    var cyl = isFinite(input.cyl) && input.cyl >= 1 ? Math.round(input.cyl) : 1;

    var gasketBore  = isFinite(input.gasketBore) && input.gasketBore > 0 ? input.gasketBore : boreIn;
    var gasketThick = isFinite(input.gasketThick) ? input.gasketThick : 0;
    var deck        = isFinite(input.deck) ? input.deck : 0;
    var pistonCc    = isFinite(input.piston) ? input.piston : 0;

    var swept  = cylinderCc(boreIn * CM_PER_INCH, strokeIn * CM_PER_INCH);
    var gasket = cylinderCc(gasketBore * CM_PER_INCH, gasketThick * CM_PER_INCH);
    var deckCc = cylinderCc(boreIn * CM_PER_INCH, deck * CM_PER_INCH);

    var clearance = input.chamber + gasket + deckCc + pistonCc;

    if (clearance <= 0) {
      return { ok: false, errors: ['That combination gives zero or negative clearance volume. Check the dome and deck figures.'] };
    }

    var cr = (swept + clearance) / clearance;
    var dispCc = swept * cyl;
    var dispCi = dispCc / CC_PER_CI;

    var status;
    if (cr < 8) {
      status = { key: 'monitor', label: num(cr, 2) + ':1',
                 note: 'Low. Typical of an older boosted build or a worn engine.' };
    } else if (cr <= 10.5) {
      status = { key: 'good', label: num(cr, 2) + ':1',
                 note: 'Pump gas friendly on most builds.' };
    } else if (cr <= 12) {
      status = { key: 'soon', label: num(cr, 2) + ':1',
                 note: 'Needs good chamber design, careful timing and probably premium fuel.' };
    } else {
      status = { key: 'now', label: num(cr, 2) + ':1',
                 note: 'Race fuel or E85 territory. Detonation risk on pump gas is high.' };
    }

    var warnings = [];
    if (deck < 0.035 && deck > -1 && gasketThick < 0.045) {
      warnings.push('Deck clearance plus gasket thickness is under 0.080 inch. That is tight for piston to valve and piston to head clearance. Check it physically with clay.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'Static compression ratio', value: num(cr, 2) + ':1',
          sub: 'Swept ' + num(swept, 1) + ' cc against ' + num(clearance, 1) + ' cc clearance.' },
        { label: 'Displacement', value: num(dispCi, 1) + ' ci',
          sub: num(dispCc, 0) + ' cc, ' + num(dispCc / 1000, 1) + ' litres across ' + cyl + ' cylinders.' }
      ],
      rows: [
        { head: 'Per cylinder' },
        { label: 'Swept volume', value: num(swept, 2) + ' cc' },
        { label: 'Chamber', value: num(input.chamber, 2) + ' cc' },
        { label: 'Head gasket', value: num(gasket, 2) + ' cc' },
        { label: 'Deck clearance', value: num(deckCc, 2) + ' cc' },
        { label: 'Piston dish or dome', value: num(pistonCc, 2) + ' cc' },
        { label: 'Total clearance', value: num(clearance, 2) + ' cc' },
        { head: 'Engine' },
        { label: 'Bore', value: num(boreIn, 3) + ' in  (' + num(boreIn * MM_PER_INCH, 2) + ' mm)' },
        { label: 'Stroke', value: num(strokeIn, 3) + ' in  (' + num(strokeIn * MM_PER_INCH, 2) + ' mm)' },
        { label: 'Cylinders', value: String(cyl) },
        { label: 'Displacement', value: num(dispCi, 1) + ' ci / ' + num(dispCc / 1000, 2) + ' L' },
        { label: 'Bore to stroke', value: num(boreIn / strokeIn, 3) + ':1' },
        { label: 'Compression ratio', value: num(cr, 2) + ':1' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "bore",
          "key": "b",
          "type": "number"
      },
      {
          "name": "boreUnit",
          "key": "bu",
          "type": "select"
      },
      {
          "name": "stroke",
          "key": "s",
          "type": "number"
      },
      {
          "name": "strokeUnit",
          "key": "su",
          "type": "select"
      },
      {
          "name": "cyl",
          "key": "n",
          "type": "number"
      },
      {
          "name": "chamber",
          "key": "ch",
          "type": "number"
      },
      {
          "name": "gasketBore",
          "key": "gb",
          "type": "number"
      },
      {
          "name": "gasketThick",
          "key": "gt",
          "type": "number"
      },
      {
          "name": "deck",
          "key": "dk",
          "type": "number"
      },
      {
          "name": "piston",
          "key": "pv",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-crc__figure">'
        + '<div class="pc-crc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-crc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-crc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-crc__row pc-crc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-crc__row">'
        + '<span class="pc-crc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-crc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_CRC = { compute: compute };
})();
